import React from 'react'
import { Avatar, Chip, Grid } from '@mui/material'
import { Error as ErrorIcon } from '@mui/icons-material'
import { PickingValidateSkuDetail } from '../../interfaces/Picking/Picking'
import { TableColumn, TableAction } from '../_EOS/GenericTable/types'
import { EnumPickingValidate } from './pickingValidateConfig'
import { makeStyles } from '@mui/styles'
import { CustomActionButtons } from './CustomActionButtons'

const useStyles = makeStyles(() => ({
  imageCell: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8
    }
  }
}))

interface PickingValidateConfigProps {
  isPickingRouteGroupedValidation: boolean
  orderCancellationRequest: boolean
  onOpenReportDiff: (row: PickingValidateSkuDetail) => void
  onOpenExcessQty: (row: PickingValidateSkuDetail) => void
  onOpenImageModal: (row: PickingValidateSkuDetail) => void
  onOpenBatches?: (row: PickingValidateSkuDetail) => void
}

export const createPickingValidateColumns = ({
  isPickingRouteGroupedValidation,
  onOpenImageModal
}: PickingValidateConfigProps): TableColumn<PickingValidateSkuDetail>[] => {
  const styles = useStyles()

  const getObservationChip = (commentary: number) => {
    const observation = EnumPickingValidate.find((obs) => obs.id === commentary)
    if (!observation) return null

    return (
      <Chip
        size='small'
        label={observation.name}
        variant='outlined'
        style={{
          backgroundColor: observation.color,
          color: observation.textColor,
          fontWeight: 'bold'
        }}
      />
    )
  }

  const getQuantityChip = (
    row: PickingValidateSkuDetail,
    field: 'pickedQty' | 'validatedQty'
  ) => {
    const observation = EnumPickingValidate.find(
      (obs) => obs.id === row.commentary
    )
    const value = row[field]

    return (
      <Chip
        size='small'
        label={value}
        variant='outlined'
        style={{
          backgroundColor: observation?.color || '',
          color: observation?.textColor || '',
          fontWeight: 'bold'
        }}
      />
    )
  }

  const columns: TableColumn<PickingValidateSkuDetail>[] = [
    {
      key: 'urlImage',
      title: 'Foto',
      width: '100px',
      render: (value: any, row: PickingValidateSkuDetail) => (
        <div className={styles.imageCell} onClick={() => onOpenImageModal(row)}>
          <Avatar
            variant='square'
            src={
              value ||
              'https://via.placeholder.com/150/e5e5e5/808080?text=No%20Image'
            }
            style={{ width: 60, height: 60 }}
          />
        </div>
      )
    },
    {
      key: 'sku',
      title: 'SKU/EAN',
      render: (_, row: PickingValidateSkuDetail) => (
        <div style={{ fontSize: '0.85rem' }}>
          {row.eaN13 === row.sku
            ? row.eaN13.toString()
            : `${row.eaN13} / ${row.sku}`}
        </div>
      )
    },
    {
      key: 'name',
      title: 'Nombre',
      render: (value: any) => <div style={{ fontSize: '0.85rem' }}>{value}</div>
    }
  ]

  // Columnas condicionales según tipo de validación
  if (!isPickingRouteGroupedValidation) {
    columns.push(
      {
        key: 'pickedQty',
        title: 'Pick',
        width: '80px',
        align: 'center',
        render: (_, row: PickingValidateSkuDetail) =>
          getQuantityChip(row, 'pickedQty')
      },
      {
        key: 'validatedQty',
        title: 'Val',
        width: '80px',
        align: 'center',
        render: (_, row: PickingValidateSkuDetail) =>
          getQuantityChip(row, 'validatedQty')
      }
    )
  } else {
    columns.push({
      key: 'realQty',
      title: 'Qty',
      width: '120px',
      align: 'center',
      render: (_, row: PickingValidateSkuDetail) => {
        const observation = EnumPickingValidate.find(
          (obs) => obs.id === row.commentary
        )

        const qtyLabel = (): string => {
          const { excessQty, commentary, pickedQty, realQty } = row
          const excess =
            excessQty !== 0 ? `(${excessQty > 0 ? '+' : ''}${excessQty})` : ''

          if (commentary === 1 || commentary === 2) {
            return pickedQty.toString() + excess
          } else {
            return `${pickedQty} de ${realQty}` + excess
          }
        }

        return (
          <Chip
            size='small'
            label={qtyLabel()}
            variant='outlined'
            style={{
              backgroundColor: observation?.color || '',
              color: observation?.textColor || '',
              fontWeight: 'bold'
            }}
          />
        )
      }
    })
  }

  // Columna de observaciones
  columns.push({
    key: 'commentary',
    title: 'Obs',
    width: '120px',
    align: 'center',
    render: (value: any) => getObservationChip(value)
  })

  return columns
}

export const createPickingValidateActions = (
  props: PickingValidateConfigProps
): TableAction<PickingValidateSkuDetail>[] => {
  return [
    {
      key: 'actions',
      label: 'INFORMAR',
      icon: <div />, // Icono placeholder, no se usa cuando hay render
      onClick: () => {}, // Placeholder, no se usa cuando hay render
      render: (row: PickingValidateSkuDetail) => (
        <CustomActionButtons
          row={row}
          orderCancellationRequest={props.orderCancellationRequest}
          isPickingRouteGroupedValidation={
            props.isPickingRouteGroupedValidation
          }
          onOpenReportDiff={props.onOpenReportDiff}
          onOpenExcessQty={props.onOpenExcessQty}
          onOpenBatches={props.onOpenBatches}
        />
      )
    }
  ]
}

// Componente personalizado para mostrar estado de recall
export const RecallIndicator: React.FC<{ row: PickingValidateSkuDetail }> = ({
  row
}) => {
  if (!row.isInRecall) return null

  return (
    <Grid
      container
      direction='row'
      alignItems='center'
      style={{ color: 'red', marginTop: 8 }}
    >
      <ErrorIcon style={{ marginRight: 8 }} />
      <span style={{ fontSize: '0.85rem' }}>
        Producto con orden de retiro activa
      </span>
    </Grid>
  )
}
